import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Tooltip,
  Divider,
  Tab,
  Tabs,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  PictureAsPdf as PdfIcon,
  Email as EmailIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  Preview as PreviewIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, startOfMonth, endOfMonth } from "date-fns";
import FinanceContainer from "../containers/FinanceContainer";
import config from "../../../config";
import {
  PREVIEW_FRAME_HEIGHT_SPLIT,
  PREVIEW_FRAME_HEIGHT_STACKED,
  PREVIEW_PANE_CHROME_HEIGHT,
  PREVIEW_STACK_BREAKPOINT_PX,
} from "../constants/previewLayout";

interface Debtor {
  id: string;
  fullName: string;
  email?: string;
  unitRef?: string;
  developmentName?: string;
  balance?: number;
}

interface StatementLog {
  id: string;
  debtorId: string;
  debtorName: string;
  sentTo: string;
  period: string;
  fromDate?: string;
  toDate?: string;
  status: "sent" | "failed" | "pending";
  errorMessage?: string;
  createdAt: string;
}

interface PreviewDebtor {
  id: string;
  fullName: string;
  email?: string;
}

const Statements: React.FC = () => {
  const isStackedPreview = useMediaQuery(
    `(max-width:${PREVIEW_STACK_BREAKPOINT_PX}px)`,
  );
  const previewFrameHeight = isStackedPreview
    ? PREVIEW_FRAME_HEIGHT_STACKED
    : PREVIEW_FRAME_HEIGHT_SPLIT;
  const previewPaneHeight = previewFrameHeight + PREVIEW_PANE_CHROME_HEIGHT;

  const [debtors, setDebtors] = useState<Debtor[]>([]);
  const [logs, setLogs] = useState<StatementLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [bulkSending, setBulkSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState<Date | null>(
    startOfMonth(new Date()),
  );
  const [toDate, setToDate] = useState<Date | null>(new Date());

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "info" });

  const [bulkDialog, setBulkDialog] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [workspaceTab, setWorkspaceTab] = useState<"dates" | "debtors">(
    "debtors",
  );
  const [previewDebtor, setPreviewDebtor] = useState<PreviewDebtor | null>(
    null,
  );
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDownloading, setPreviewDownloading] = useState(false);
  const [previewEmailing, setPreviewEmailing] = useState(false);
  const [previewMode, setPreviewMode] = useState<"single" | "both">("single");
  const [previewSection, setPreviewSection] = useState<"debtor" | "vat">(
    "debtor",
  );
  const [normalPreviewUrl, setNormalPreviewUrl] = useState<string | null>(null);
  const [vatPreviewUrl, setVatPreviewUrl] = useState<string | null>(null);

  const fetchDebtors = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(`${config.API_BASE_URL}/debtors`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDebtors(data);
      }
    } catch (error) {
      console.error("Error fetching debtors:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(
        `${config.API_BASE_URL}/statements/logs?limit=50`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDebtors();
    fetchLogs();
  }, [fetchDebtors, fetchLogs]);

  const createStatementParams = () => {
    const params = new URLSearchParams();
    if (fromDate) params.append("fromDate", format(fromDate, "yyyy-MM-dd"));
    if (toDate) params.append("toDate", format(toDate, "yyyy-MM-dd"));
    return params;
  };

  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const clearPreviewUrls = () => {
    if (normalPreviewUrl) {
      window.URL.revokeObjectURL(normalPreviewUrl);
    }
    if (vatPreviewUrl) {
      window.URL.revokeObjectURL(vatPreviewUrl);
    }
    setNormalPreviewUrl(null);
    setVatPreviewUrl(null);
  };

  const handleDownloadPdf = async (debtorId: string, debtorName: string) => {
    try {
      setDownloadingId(debtorId);
      const token = localStorage.getItem("vms_access_token");
      const params = createStatementParams();

      const response = await fetch(
        `${config.API_BASE_URL}/debtors/${debtorId}/statement/pdf?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const blob = await response.blob();
        downloadBlob(blob, `Statement_${debtorName.replace(/\s+/g, "_")}.pdf`);
        setSnackbar({
          open: true,
          message: "PDF downloaded successfully",
          severity: "success",
        });
      } else {
        throw new Error("Failed to generate PDF");
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to download PDF",
        severity: "error",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSendEmail = async (debtorId: string) => {
    try {
      setSendingId(debtorId);
      const token = localStorage.getItem("vms_access_token");
      const params = createStatementParams();

      const response = await fetch(
        `${config.API_BASE_URL}/debtors/${debtorId}/statement/email?${params}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const result = await response.json();
        setSnackbar({
          open: true,
          message: `Statement sent to ${result.sentTo}`,
          severity: "success",
        });
        fetchLogs();
      } else {
        const error = await response.json();
        throw new Error(error.message || "Failed to send email");
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to send email",
        severity: "error",
      });
    } finally {
      setSendingId(null);
    }
  };

  const handleOpenPreview = async (debtor: PreviewDebtor) => {
    try {
      setPreviewDebtor(debtor);
      setPreviewLoading(true);
      clearPreviewUrls();

      const token = localStorage.getItem("vms_access_token");
      const params = createStatementParams();

      const [normalRes, vatRes] = await Promise.all([
        fetch(
          `${config.API_BASE_URL}/debtors/${debtor.id}/statement/pdf?${params}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
        fetch(
          `${config.API_BASE_URL}/debtors/${debtor.id}/vat-statement/pdf?${params}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
      ]);

      let loadedAny = false;

      if (normalRes.ok) {
        const normalBlob = await normalRes.blob();
        setNormalPreviewUrl(window.URL.createObjectURL(normalBlob));
        loadedAny = true;
      }

      if (vatRes.ok) {
        const vatBlob = await vatRes.blob();
        setVatPreviewUrl(window.URL.createObjectURL(vatBlob));
        loadedAny = true;
      }

      if (!loadedAny) {
        throw new Error("Failed to load statement previews");
      }

      if (!normalRes.ok || !vatRes.ok) {
        setSnackbar({
          open: true,
          message:
            "One preview failed to load. You can still use available actions.",
          severity: "info",
        });
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to open previews",
        severity: "error",
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSelectDebtor = (
    debtor: PreviewDebtor,
    mode: "single" | "both" = "single",
  ) => {
    setPreviewMode(mode);
    if (mode === "single") {
      setPreviewSection("debtor");
    }
    void handleOpenPreview(debtor);
  };

  const handlePreviewDownloadBoth = async () => {
    if (!previewDebtor) return;

    try {
      setPreviewDownloading(true);
      const token = localStorage.getItem("vms_access_token");
      const params = createStatementParams();

      const [normalRes, vatRes] = await Promise.all([
        fetch(
          `${config.API_BASE_URL}/debtors/${previewDebtor.id}/statement/pdf?${params}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
        fetch(
          `${config.API_BASE_URL}/debtors/${previewDebtor.id}/vat-statement/pdf?${params}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
      ]);

      let downloaded = 0;

      if (normalRes.ok) {
        const normalBlob = await normalRes.blob();
        downloadBlob(
          normalBlob,
          `Statement_${previewDebtor.fullName.replace(/\s+/g, "_")}.pdf`,
        );
        downloaded += 1;
      }

      if (vatRes.ok) {
        const vatBlob = await vatRes.blob();
        downloadBlob(
          vatBlob,
          `VAT_Statement_${previewDebtor.fullName.replace(/\s+/g, "_")}.pdf`,
        );
        downloaded += 1;
      }

      if (downloaded === 0) {
        throw new Error("Failed to download both statements");
      }

      setSnackbar({
        open: true,
        message:
          downloaded === 2
            ? "Normal and VAT statements downloaded"
            : "Only one statement downloaded",
        severity: downloaded === 2 ? "success" : "info",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to download both statements",
        severity: "error",
      });
    } finally {
      setPreviewDownloading(false);
    }
  };

  const handlePreviewEmailBoth = async () => {
    if (!previewDebtor) return;

    try {
      setPreviewEmailing(true);
      const token = localStorage.getItem("vms_access_token");
      const params = createStatementParams();

      const [normalRes, vatRes] = await Promise.all([
        fetch(
          `${config.API_BASE_URL}/debtors/${previewDebtor.id}/statement/email?${params}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        ),
        fetch(
          `${config.API_BASE_URL}/debtors/${previewDebtor.id}/vat-statement/email?${params}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        ),
      ]);

      const sentCount = [normalRes.ok, vatRes.ok].filter(Boolean).length;

      if (sentCount === 0) {
        throw new Error("Failed to send both statements");
      }

      setSnackbar({
        open: true,
        message:
          sentCount === 2
            ? "Normal and VAT statements emailed"
            : "Only one statement email sent",
        severity: sentCount === 2 ? "success" : "info",
      });
      fetchLogs();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to email both statements",
        severity: "error",
      });
    } finally {
      setPreviewEmailing(false);
    }
  };

  const handleBulkSend = async () => {
    try {
      setBulkSending(true);
      const token = localStorage.getItem("vms_access_token");

      const response = await fetch(
        `${config.API_BASE_URL}/statements/bulk-send`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fromDate: fromDate ? format(fromDate, "yyyy-MM-dd") : undefined,
            toDate: toDate ? format(toDate, "yyyy-MM-dd") : undefined,
          }),
        },
      );

      if (response.ok) {
        const result = await response.json();

        const dualSentCount = Array.isArray(result.results)
          ? result.results.filter(
              (r: any) => r?.normalStatement && r?.vatStatement,
            ).length
          : result.sent || 0;

        setSnackbar({
          open: true,
          message: `Bulk send complete: ${dualSentCount} debtors received BOTH Normal + VAT statements, ${result.failed} failed out of ${result.total}`,
          severity: result.failed > 0 ? "info" : "success",
        });
        fetchLogs();
        setBulkDialog(false);
      } else {
        const error = await response.json();
        throw new Error(error.message || "Bulk send failed");
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Bulk send failed",
        severity: "error",
      });
    } finally {
      setBulkSending(false);
    }
  };

  const filteredDebtors = debtors.filter(
    (debtor) =>
      debtor.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      debtor.unitRef?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      debtor.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      debtor.developmentName?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined || value === null) return "-";
    return `$${value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <FinanceContainer>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h4" fontWeight={600}>
              Statement Manager
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => setHistoryDialog(true)}
              >
                Send History
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={() => setBulkDialog(true)}
              >
                Bulk Send
              </Button>
            </Box>
          </Box>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Tabs
                value={workspaceTab}
                onChange={(_, nextValue: "dates" | "debtors") =>
                  setWorkspaceTab(nextValue)
                }
                sx={{ mb: 2 }}
              >
                <Tab label="Date selection" value="dates" />
                <Tab label="Debtor listing" value="debtors" />
              </Tabs>

              {workspaceTab === "dates" ? (
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="From Date"
                      value={fromDate}
                      onChange={(date) => setFromDate(date)}
                      slotProps={{
                        textField: { fullWidth: true, size: "small" },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="To Date"
                      value={toDate}
                      onChange={(date) => setToDate(date)}
                      slotProps={{
                        textField: { fullWidth: true, size: "small" },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by name, unit, email or project..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  Click a debtor row to load statements in the embedded viewer.
                  Use "View Both" to display Debtor and VAT side by side.
                </Typography>
              )}
            </CardContent>
          </Card>

          {workspaceTab === "debtors" && (
            <>
              <Card sx={{ mb: 3 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell>Name</TableCell>
                        <TableCell>Unit / Project</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="right">Balance</TableCell>
                        <TableCell align="center" sx={{ width: 200 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : filteredDebtors.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No debtors found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredDebtors.map((debtor) => (
                          <TableRow
                            key={debtor.id}
                            hover
                            onClick={() =>
                              handleSelectDebtor({
                                id: debtor.id,
                                fullName: debtor.fullName,
                                email: debtor.email,
                              })
                            }
                            selected={previewDebtor?.id === debtor.id}
                            sx={{ cursor: "pointer" }}
                          >
                            <TableCell>
                              <Typography fontWeight={500}>
                                {debtor.fullName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {debtor.unitRef || "-"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {debtor.developmentName || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {debtor.email ? (
                                <Chip
                                  label={debtor.email}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                />
                              ) : (
                                <Chip
                                  label="No email"
                                  size="small"
                                  color="warning"
                                />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                fontWeight={600}
                                color={
                                  (debtor.balance ?? 0) > 0
                                    ? "error.main"
                                    : "success.main"
                                }
                              >
                                {formatCurrency(debtor.balance)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  gap: 1,
                                }}
                              >
                                <Tooltip title="Download PDF">
                                  <IconButton
                                    color="primary"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleDownloadPdf(
                                        debtor.id,
                                        debtor.fullName,
                                      );
                                    }}
                                    disabled={
                                      downloadingId === debtor.id ||
                                      sendingId === debtor.id
                                    }
                                  >
                                    {downloadingId === debtor.id ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <DownloadIcon />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="View Both">
                                  <IconButton
                                    color="info"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleSelectDebtor(
                                        {
                                          id: debtor.id,
                                          fullName: debtor.fullName,
                                          email: debtor.email,
                                        },
                                        "both",
                                      );
                                    }}
                                    disabled={
                                      downloadingId === debtor.id ||
                                      sendingId === debtor.id ||
                                      (previewLoading &&
                                        previewDebtor?.id === debtor.id)
                                    }
                                  >
                                    {previewLoading &&
                                    previewDebtor?.id === debtor.id ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <PreviewIcon />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    debtor.email
                                      ? "Send via Email"
                                      : "No email address"
                                  }
                                >
                                  <span>
                                    <IconButton
                                      color="secondary"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleSendEmail(debtor.id);
                                      }}
                                      disabled={
                                        !debtor.email ||
                                        sendingId === debtor.id ||
                                        downloadingId === debtor.id
                                      }
                                    >
                                      {sendingId === debtor.id ? (
                                        <CircularProgress size={20} />
                                      ) : (
                                        <EmailIcon />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>

              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6">
                      Statements Viewer
                      {previewDebtor ? ` - ${previewDebtor.fullName}` : ""}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        variant={
                          previewMode === "single" ? "contained" : "outlined"
                        }
                        onClick={() => setPreviewMode("single")}
                      >
                        Current Tab
                      </Button>
                      <Button
                        size="small"
                        variant={
                          previewMode === "both" ? "contained" : "outlined"
                        }
                        onClick={() => setPreviewMode("both")}
                      >
                        View Both
                      </Button>
                    </Box>
                  </Box>

                  <Tabs
                    value={previewSection}
                    onChange={(_, nextValue: "debtor" | "vat") =>
                      setPreviewSection(nextValue)
                    }
                    sx={{ mb: 2 }}
                  >
                    <Tab label="Debtor statement" value="debtor" />
                    <Tab label="VAT statement" value="vat" />
                  </Tabs>

                  <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={
                        previewDownloading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DownloadIcon />
                        )
                      }
                      onClick={handlePreviewDownloadBoth}
                      disabled={
                        previewLoading ||
                        previewDownloading ||
                        previewEmailing ||
                        !previewDebtor
                      }
                    >
                      {previewDownloading ? "Downloading..." : "Download Both"}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={
                        previewEmailing ? (
                          <CircularProgress size={20} />
                        ) : (
                          <SendIcon />
                        )
                      }
                      onClick={handlePreviewEmailBoth}
                      disabled={
                        previewLoading ||
                        previewDownloading ||
                        previewEmailing ||
                        !previewDebtor?.email
                      }
                    >
                      {previewEmailing ? "Emailing..." : "Email Both"}
                    </Button>
                  </Box>

                  {previewLoading ? (
                    <Box
                      sx={{
                        height: previewFrameHeight,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : !previewDebtor ? (
                    <Alert severity="info">
                      Select a debtor from the list to load statements.
                    </Alert>
                  ) : previewMode === "both" ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} lg={6}>
                        <Paper
                          variant="outlined"
                          sx={{ p: 1, height: previewPaneHeight }}
                        >
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Debtor Statement
                          </Typography>
                          {normalPreviewUrl ? (
                            <Box
                              component="iframe"
                              src={normalPreviewUrl}
                              title="Debtor Statement Preview"
                              sx={{
                                width: "100%",
                                height: previewFrameHeight,
                                border: 0,
                              }}
                            />
                          ) : (
                            <Alert severity="warning">
                              Debtor preview unavailable.
                            </Alert>
                          )}
                        </Paper>
                      </Grid>
                      <Grid item xs={12} lg={6}>
                        <Paper
                          variant="outlined"
                          sx={{ p: 1, height: previewPaneHeight }}
                        >
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            VAT Statement
                          </Typography>
                          {vatPreviewUrl ? (
                            <Box
                              component="iframe"
                              src={vatPreviewUrl}
                              title="VAT Statement Preview"
                              sx={{
                                width: "100%",
                                height: previewFrameHeight,
                                border: 0,
                              }}
                            />
                          ) : (
                            <Alert severity="warning">
                              VAT preview unavailable.
                            </Alert>
                          )}
                        </Paper>
                      </Grid>
                    </Grid>
                  ) : (
                    <Paper
                      variant="outlined"
                      sx={{ p: 1, height: previewPaneHeight }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {previewSection === "debtor"
                          ? "Debtor Statement"
                          : "VAT Statement"}
                      </Typography>
                      {previewSection === "debtor" ? (
                        normalPreviewUrl ? (
                          <Box
                            component="iframe"
                            src={normalPreviewUrl}
                            title="Debtor Statement Preview"
                            sx={{
                              width: "100%",
                              height: previewFrameHeight,
                              border: 0,
                            }}
                          />
                        ) : (
                          <Alert severity="warning">
                            Debtor preview unavailable.
                          </Alert>
                        )
                      ) : vatPreviewUrl ? (
                        <Box
                          component="iframe"
                          src={vatPreviewUrl}
                          title="VAT Statement Preview"
                          sx={{
                            width: "100%",
                            height: previewFrameHeight,
                            border: 0,
                          }}
                        />
                      ) : (
                        <Alert severity="warning">
                          VAT preview unavailable.
                        </Alert>
                      )}
                    </Paper>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* Bulk Send Dialog */}
          <Dialog
            open={bulkDialog}
            onClose={() => !bulkSending && setBulkDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Bulk Send Statements</DialogTitle>
            <DialogContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                This will send BOTH Normal and VAT statements to all active
                debtors with email addresses on file.
              </Alert>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    onChange={(date) => setFromDate(date)}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    onChange={(date) => setToDate(date)}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                </Grid>
              </Grid>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Both statement reports will be generated for the period{" "}
                {fromDate ? format(fromDate, "dd MMM yyyy") : "start"} to{" "}
                {toDate ? format(toDate, "dd MMM yyyy") : "today"}.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setBulkDialog(false)}
                disabled={bulkSending}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleBulkSend}
                disabled={bulkSending}
                startIcon={
                  bulkSending ? <CircularProgress size={20} /> : <SendIcon />
                }
              >
                {bulkSending ? "Sending both..." : "Send Both to All"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* History Dialog */}
          <Dialog
            open={historyDialog}
            onClose={() => setHistoryDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6">Statement Send History</Typography>
                <IconButton onClick={fetchLogs} disabled={logsLoading}>
                  <RefreshIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell>Date</TableCell>
                      <TableCell>Debtor</TableCell>
                      <TableCell>Sent To</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logsLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : logs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">
                            No statements sent yet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      logs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            {format(
                              new Date(log.createdAt),
                              "dd MMM yyyy HH:mm",
                            )}
                          </TableCell>
                          <TableCell>{log.debtorName}</TableCell>
                          <TableCell>{log.sentTo}</TableCell>
                          <TableCell>{log.period}</TableCell>
                          <TableCell align="center">
                            {log.status === "sent" ? (
                              <Chip
                                icon={<CheckIcon />}
                                label="Sent"
                                size="small"
                                color="success"
                              />
                            ) : log.status === "failed" ? (
                              <Tooltip
                                title={log.errorMessage || "Unknown error"}
                              >
                                <Chip
                                  icon={<ErrorIcon />}
                                  label="Failed"
                                  size="small"
                                  color="error"
                                />
                              </Tooltip>
                            ) : (
                              <Chip
                                label="Pending"
                                size="small"
                                color="warning"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setHistoryDialog(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={5000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </LocalizationProvider>
    </FinanceContainer>
  );
};

export default Statements;
